import { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@carbon/react';
import { Add } from '@carbon/react/icons';
import { PageHeader } from 'components/page-header';
import { pageTitle } from 'utils/page-title';
import AreasCatalogContent from './areas-catalog-content';
import { AreasSpatialExport } from './areas-spatial-export';
import type { AreaGeometriesQueryParams } from './atoms';
import { CreateAreaPanel } from 'components/create-area-panel/create-area-panel';
import type { SelectedArea as CheckedRow } from 'components/create-area-panel/create-area-types';
import {
  useMapWorkspaceState,
  useResetMapWorkspaceOnUnmount,
} from 'components/create-area-panel/hooks';
import { useGeometryUpload } from 'lib/area-geometry-uploads';
import style from './styles.module.scss';

/**
 * Areas catalog page. The shell owns the title, the Create action and the creation
 * panel; the table, filters and pagination live in `AreasCatalogContent`.
 *
 * @return {JSX.Element} The Areas catalog page.
 */
export const ListAreasPage = () => {
  const [checkedRows, setCheckedRows] = useState<CheckedRow[]>([]);
  // The current catalog query params + filtered total, reported up by the table
  // so the header export buttons scope to — and enable against — exactly what the
  // table is showing.
  const [catalogState, setCatalogState] = useState<{
    params: AreaGeometriesQueryParams;
    totalCount: number;
  }>({ params: {}, totalCount: 0 });
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // The Create panel and the catalog map are siblings, so what the user draws for a
  // new area is held between them, in the shared form-to-map state.
  const { drawing, reset: resetDrawing, resetAll } = useMapWorkspaceState();
  // Story 4: the boundary files, sent and checked on the server.
  const upload = useGeometryUpload();
  const { set: setUploadFiles } = upload;
  useResetMapWorkspaceOnUnmount(resetAll);
  // The area the panel just created, so the catalog can refresh and offer a way
  // to open it. Cleared once the catalog has reported it.
  const [createdArea, setCreatedArea] = useState<{
    id: number;
    name: string;
    businessId: string;
  } | null>(null);

  const handleCheckedRowsChange = useCallback((rows: CheckedRow[]) => {
    setCheckedRows(rows);
  }, []);

  // Closing the panel abandons the drawing along with the rest of the form, and
  // hands the map back at its usual size.
  // The files go with the panel, which stops their status reads and channels. A
  // create closes the panel too.
  const handleCloseCreate = useCallback(() => {
    setIsCreateOpen(false);
    resetDrawing();
    setUploadFiles([]);
  }, [resetDrawing, setUploadFiles]);

  return (
    <>
      <Helmet>
        <title>{pageTitle('Areas')}</title>
      </Helmet>
      <div className={style.areaGeometriesPageHeader}>
        {/*
          No breadcrumbs: a single "Areas" crumb pointing at the page you're
          already on only repeats the title. Matches the AFE Catalog header.
        */}
        <PageHeader
          data-testid="area-geometries-page-header"
          title="Areas"
          breadcrumbs={[]}
          actions={
            <div className={style.headerActions}>
              <AreasSpatialExport
                params={catalogState.params}
                totalCount={catalogState.totalCount}
              />
              <Button
                kind="primary"
                size="md"
                renderIcon={Add}
                onClick={() => setIsCreateOpen(true)}
              >
                {checkedRows.length > 0
                  ? `Create New Area (${checkedRows.length})`
                  : 'Create New Area'}
              </Button>
            </div>
          }
        />
      </div>
      <div className={style.mainContent}>
        <AreasCatalogContent
          onCheckedRowsChange={handleCheckedRowsChange}
          onCatalogStateChange={setCatalogState}
          drawing={drawing}
          createdArea={createdArea}
          onCreatedAreaDismiss={() => setCreatedArea(null)}
        />
      </div>
      <CreateAreaPanel
        open={isCreateOpen}
        onClose={handleCloseCreate}
        onCreated={setCreatedArea}
        selectedAreas={checkedRows}
        drawing={drawing}
        upload={upload}
      />
    </>
  );
};

export default ListAreasPage;
